import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/primitives/button";
import type { CreditBalanceTrial } from "@/server/types/subscription";

/** The trial allowance is a fixed 60-credit budget ($1.98 at the standard $0.033/credit rate). */
const TRIAL_TOTAL_CREDITS = 60;

interface TrialStripProps {
  /** Trial node from the /credit-balance DTO. null/undefined = paid user or no trial → render nothing. */
  trial: CreditBalanceTrial | null | undefined;
  /** enforced mirror from the DTO. Drives hard-wall vs shadow credits strip. */
  enforced: boolean | undefined;
  /** Invoked from the trial-ended prompt to open the plans / checkout. */
  onSubscribe: () => void;
}

/**
 * Trial state surface driven entirely by the /credit-balance DTO
 * (free-trial-contract@2 §4 + §6).
 *
 * The trial is credit-only: 60 one-time credits, no time expiry. `trial.endsAt`
 * still exists in the DTO but carries a far-future sentinel (e.g. 2099-01-01)
 * — NOTHING in this component may be derived from it (no countdown, no days,
 * no deadlines).
 *
 * Rendering rules (getting these wrong is a user-visible billing bug). This
 * MUST mirror A1's gate (contract §3.1 / §4) so the screen never contradicts the
 * bot. Precedence:
 *  - trial null/absent  → render nothing (paid user, or an old BE that omits the field).
 *  - enforced && bypassed → NO wall (operator exemption; the bot serves them
 *    regardless of trial state) → fall through to the normal credits UI.
 *  - enforced && locked → "access paused — contact support" (operator hard-lock;
 *    subscribing will NOT un-lock them, so we do NOT show the subscribe wall).
 *  - enforced && !active → HARD subscribe WALL (trial credits used up → subscribe).
 *    The wall is driven ONLY off active/enforced, NEVER off creditsRemaining (a
 *    round1 display value that can read 0 while the trial is still active).
 *  - otherwise (active, or shadow with active=false) → credits line only,
 *    NEVER a wall (in shadow mode the assistant is still answering).
 */
export default function TrialStrip({
  trial,
  enforced,
  onSubscribe,
}: TrialStripProps) {
  const { t } = useTranslation();

  // Paid user, or an un-upgraded BE that doesn't send the trial node.
  if (!trial) {
    return null;
  }

  const isEnforced = enforced === true;

  // Operator exemption: the bot serves a bypassed user regardless of trial
  // state, so we must NEVER wall them — fall through to the normal credits UI
  // as if the trial were live (no subscribe wall, no paused screen).
  const isBypassed = isEnforced && trial.bypassed === true;

  // Operator hard-lock: enforced + locked (and NOT bypassed) → "access paused"
  // screen, NOT the subscribe wall. Subscribing won't un-lock them; only an
  // admin can. Checked before the credit wall so a locked user whose trial has
  // ended sees the paused message, not the subscribe CTA.
  const showLocked = isEnforced && !isBypassed && trial.locked === true;

  // Hard subscribe wall ONLY when the enforcer is live, the trial is no longer
  // active, and the user is neither bypassed nor locked.
  const showWall =
    isEnforced && !isBypassed && !showLocked && trial.active === false;

  if (showLocked) {
    return (
      <div className="my-2 flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-secondary/40 bg-secondary/5 px-5 py-4">
        <div className="flex flex-wrap items-baseline gap-2.5">
          <span className="text-[9.5px] font-semibold uppercase tracking-[0.16em] text-secondary">
            {t("subscription.trialLockedEyebrow")}
          </span>
          <h4 className="text-sm font-semibold">
            {t("subscription.trialLockedTitle")}
          </h4>
          <p className="w-full text-[11.5px] text-muted-foreground">
            {t("subscription.trialLockedBody")}
          </p>
        </div>
      </div>
    );
  }

  if (showWall) {
    return (
      <div className="my-2 flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-secondary/40 bg-secondary/5 px-5 py-4">
        <div className="flex flex-wrap items-baseline gap-2.5">
          <span className="text-[9.5px] font-semibold uppercase tracking-[0.16em] text-secondary">
            {t("subscription.trialEndedEyebrow")}
          </span>
          <h4 className="text-sm font-semibold">
            {t("subscription.trialEndedTitle")}
          </h4>
          <p className="w-full text-[11.5px] text-muted-foreground">
            {t("subscription.trialEndedBody", { total: TRIAL_TOTAL_CREDITS })}
          </p>
        </div>
        <Button
          onClick={onSubscribe}
          size="sm"
          className="gap-1.5 text-[12.5px] font-semibold"
        >
          {t("subscription.trialEndedCta")}
          <ArrowRight size={14} />
        </Button>
      </div>
    );
  }

  // Credits line — active trial, or shadow mode (bot still answering). The
  // trial is credit-only: no countdown, and nothing derived from trial.endsAt
  // (it carries a far-future sentinel, not a real deadline).
  // Floor (never round up) so a remaining-budget display never overstates what
  // the user actually has — matches StrandedCreditsPanel's Math.floor(balance).
  // Guard non-finite values from a malformed DTO. Display-only — NOT gating.
  const creditsRemaining = Number.isFinite(trial.creditsRemaining)
    ? Math.max(0, Math.floor(trial.creditsRemaining))
    : 0;

  return (
    <div className="my-2 flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-dashed border-secondary/40 bg-secondary/[0.04] px-5 py-4">
      <div className="flex flex-wrap items-baseline gap-2.5">
        <span className="inline-flex items-center gap-1.5 text-[9.5px] font-semibold uppercase tracking-[0.16em] text-secondary">
          <Sparkles size={11} className="shrink-0" />
          {t("subscription.trialCreditsEyebrow")}
        </span>
        <p className="w-full text-[11.5px] text-muted-foreground">
          {t("subscription.trialCreditsLine", {
            remaining: creditsRemaining,
            total: TRIAL_TOTAL_CREDITS,
          })}
        </p>
      </div>
    </div>
  );
}
